import { authService } from './authService';

// API configuration
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '' // Use relative URLs in production
  : 'http://localhost:8080';

interface ActivityEntry {
  id: string;
  timestamp: number;
  event: 'mouse_move' | 'page_enter' | 'page_leave' | 'idle_start' | 'idle_end';
  page: string;
  idleDuration?: number;
}

interface DailySummary {
  date: string;
  totalActiveTime: number;
  totalIdleTime: number;
  totalPageVisits: number;
  pagesVisited: string[];
  lastActivity: Date;
}

class ActivityTracker {
  private readonly IDLE_THRESHOLD_MS = 30000; // 30 seconds of no movement = idle
  private readonly TRACKING_INTERVAL_MS = 1000; // Check every second
  
  private isTracking = false;
  private lastMouseMove = 0;
  private currentPage = '';
  private idleStartTime: number | null = null;
  private isCurrentlyIdle = false;
  private trackingInterval: NodeJS.Timeout | null = null;
  private currentUsername = '';
  private lastMouseMoveLogTime = 0; // For throttling mouse move events
  private lastActivity = Date.now();
  private idleThreshold = 30000; // 30 seconds
  private idleTimer: NodeJS.Timeout | null = null;
  private mouseMoveThrottle = 5000; // 5 seconds
  private pageEnterTime = 0;
  private isIdle = false;
  private username = '';
  private retentionDays = 30;
  private isManager = false;

  private get storageKey() {
    return `alphoric_activity_tracking_${this.username}`;
  }

  async init() {
    const user = authService.getCurrentUser();
    if (!user || user.userType !== 'manager') {
      this.isManager = false;
      return;
    }

    this.username = user.username;
    this.isManager = true;
    
    // Only start tracking if manager is clocked in
    await this.checkClockStatusAndStartTracking();
    
    // Check clock status periodically (every 30 seconds)
    setInterval(() => {
      this.checkClockStatusAndStartTracking();
    }, 30000);
  }

  private async checkClockStatusAndStartTracking() {
    if (!this.isManager) return;

    try {
      // Use API call to get clock status instead of authService method
      const response = await fetch(`/api/managers/${this.username}/clock-status`);
      const clockStatus = await response.json();
      
      if (clockStatus.success && clockStatus.data.isClockedIn && !this.isTracking) {
        // Manager just clocked in, start tracking
        this.startTracking();
      } else if (clockStatus.success && !clockStatus.data.isClockedIn && this.isTracking) {
        // Manager clocked out, stop tracking
        this.stopTracking();
      }
    } catch (error) {
      console.error('Error checking clock status:', error);
      // If we can't check status, stop tracking to be safe
      if (this.isTracking) {
        this.stopTracking();
      }
    }
  }

  private startTracking() {
    if (this.isTracking || !this.isManager) return;
    
    this.isTracking = true;
    this.lastActivity = Date.now();
    this.currentPage = window.location.pathname;
    this.pageEnterTime = Date.now();
    
    // Log page enter
    this.logActivity('page_enter', this.currentPage);
    
    // Set up event listeners
    this.setupEventListeners();
    
    // Start idle detection
    this.startIdleDetection();
    
    console.log('Activity tracking started - manager is clocked in');
  }

  private stopTracking() {
    if (!this.isTracking) return;
    
    // Log page leave if we were on a page
    if (this.currentPage && this.pageEnterTime > 0) {
      this.logActivity('page_leave', this.currentPage);
    }
    
    // Clean up
    this.removeEventListeners();
    this.stopIdleDetection();
    
    this.isTracking = false;
    this.currentPage = '';
    this.pageEnterTime = 0;
    
    console.log('Activity tracking stopped - manager is clocked out');
  }

  private setupEventListeners() {
    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('keypress', this.handleActivity);
    document.addEventListener('click', this.handleActivity);
    document.addEventListener('scroll', this.handleActivity);
    
    // Listen for page navigation
    window.addEventListener('beforeunload', this.handlePageLeave);
    window.addEventListener('popstate', this.handlePageChange);
  }

  private removeEventListeners() {
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('keypress', this.handleActivity);
    document.removeEventListener('click', this.handleActivity);
    document.removeEventListener('scroll', this.handleActivity);
    
    window.removeEventListener('beforeunload', this.handlePageLeave);
    window.removeEventListener('popstate', this.handlePageChange);
  }

  private handleMouseMove = (event: MouseEvent) => {
    if (!this.isTracking) return;
    
    this.updateActivity();
    
    // Throttle mouse move logging
    const now = Date.now();
    if (now - this.lastMouseMoveLogTime >= this.mouseMoveThrottle) {
      this.logActivity('mouse_move', window.location.pathname);
      this.lastMouseMoveLogTime = now;
    }
  };

  private handleActivity = () => {
    if (!this.isTracking) return;
    this.updateActivity();
  };

  private handlePageLeave = () => {
    if (!this.isTracking) return;
    if (this.currentPage && this.pageEnterTime > 0) {
      this.logActivity('page_leave', this.currentPage);
    }
  };

  private handlePageChange = () => {
    if (!this.isTracking) return;
    
    const newPage = window.location.pathname;
    if (newPage !== this.currentPage) {
      // Log leave from old page
      if (this.currentPage && this.pageEnterTime > 0) {
        this.logActivity('page_leave', this.currentPage);
      }
      
      // Log enter to new page
      this.currentPage = newPage;
      this.pageEnterTime = Date.now();
      this.logActivity('page_enter', this.currentPage);
    }
  };

  private updateActivity() {
    if (!this.isTracking) return;
    
    this.lastActivity = Date.now();
    
    if (this.isIdle && this.idleStartTime !== null) {
      const idleDuration = this.lastActivity - this.idleStartTime;
      this.logActivity('idle_end', window.location.pathname, idleDuration);
      this.isIdle = false;
      this.idleStartTime = 0;
    }
    
    this.resetIdleTimer();
  }

  private startIdleDetection() {
    this.resetIdleTimer();
  }

  private stopIdleDetection() {
    if (this.idleTimer !== null) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }

  private resetIdleTimer() {
    if (!this.isTracking) return;
    
    if (this.idleTimer !== null) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
    
    this.idleTimer = setTimeout(() => {
      if (this.isTracking && !this.isIdle) {
        this.isIdle = true;
        this.idleStartTime = Date.now();
        this.logActivity('idle_start', window.location.pathname);
      }
    }, this.idleThreshold);
  }

  private async logActivity(event: string, page: string, idleDuration?: number) {
    if (!this.isTracking || !this.isManager) return;

    try {
      // Send to server
      const user = authService.getCurrentUser();
      if (!user) return;
      
      await fetch('/api/managers/activity', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: user.username,
          page: page,
          eventType: event,
          idleDurationMs: idleDuration
        })
      });

    } catch (error) {
      console.error('Error logging activity to server:', error);
      
      // Fallback to localStorage for development
      this.logActivityToStorage(event, page, idleDuration);
    }
  }

  private logActivityToStorage(event: string, page: string, idleDuration?: number) {
    try {
      const activities = this.getStoredActivities();
      const entry: ActivityEntry = {
        id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: Date.now(),
        event: event as ActivityEntry['event'],
        page,
        idleDuration
      };

      activities.push(entry);
      
      // Keep only recent activities (30 days)
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - this.retentionDays);
      const filteredActivities = activities.filter(
        activity => new Date(activity.timestamp) > cutoffDate
      );

      localStorage.setItem(this.storageKey, JSON.stringify(filteredActivities));
    } catch (error) {
      console.error('Error storing activity:', error);
    }
  }

  private getStoredActivities(): ActivityEntry[] {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  // Public method to handle navigation changes in React Router
  handleNavigation(newPath: string) {
    if (!this.isTracking) return;
    
    if (newPath !== this.currentPage) {
      // Log leave from old page
      if (this.currentPage && this.pageEnterTime > 0) {
        this.logActivity('page_leave', this.currentPage);
      }
      
      // Log enter to new page
      this.currentPage = newPath;
      this.pageEnterTime = Date.now();
      this.logActivity('page_enter', this.currentPage);
    }
  }

  // Get daily summary for a specific date
  getDailySummary(date: string): DailySummary | null {
    const activities = this.getStoredActivities();
    const targetDate = new Date(date);
    const dateStr = targetDate.toISOString().split('T')[0];
    
    const dayActivities = activities.filter(activity => {
      const activityDate = new Date(activity.timestamp).toISOString().split('T')[0];
      return activityDate === dateStr;
    });

    if (dayActivities.length === 0) return null;

    let totalIdleTime = 0;
    let totalPageVisits = 0;
    const pagesVisited = new Set<string>();
    let lastActivity = new Date(0);

    dayActivities.forEach(activity => {
      if (activity.event === 'idle_end' && activity.idleDuration) {
        totalIdleTime += activity.idleDuration;
      }
      
      if (activity.event === 'page_enter') {
        totalPageVisits++;
        pagesVisited.add(activity.page);
      }

      if (new Date(activity.timestamp) > lastActivity) {
        lastActivity = new Date(activity.timestamp);
      }
    });

    return {
      date: dateStr,
      totalActiveTime: 0, // Will be calculated from session time - idle time
      totalIdleTime,
      totalPageVisits,
      pagesVisited: Array.from(pagesVisited),
      lastActivity
    };
  }

  // Clean up old data
  cleanup() {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - this.retentionDays);
    
    const activities = this.getStoredActivities();
    const filteredActivities = activities.filter(
      activity => new Date(activity.timestamp) > cutoffDate
    );
    
    localStorage.setItem(this.storageKey, JSON.stringify(filteredActivities));
  }

  // Check if currently tracking (for debugging)
  isCurrentlyTracking(): boolean {
    return this.isTracking;
  }

  // Get current tracking status
  getTrackingStatus() {
    return {
      isTracking: this.isTracking,
      isManager: this.isManager,
      username: this.username,
      currentPage: this.currentPage,
      isIdle: this.isIdle,
      lastActivity: new Date(this.lastActivity)
    };
  }

  /**
   * Get daily activity summary for a user (super admin only)
   */
  async getDailyActivitySummary(username: string, date: string): Promise<DailySummary | null> {
    try {
      const summary = await authService.getActivitySummary(username, date);
      
      if (!summary) {
        return {
          date,
          totalActiveTime: 0,
          totalIdleTime: 0,
          totalPageVisits: 0,
          pagesVisited: [],
          lastActivity: new Date(0)
        };
      }

      return {
        date: summary.date,
        totalActiveTime: summary.totalActiveTimeMs || 0,
        totalIdleTime: summary.totalIdleTimeMs || 0,
        totalPageVisits: Object.keys(summary.pageVisits || {}).length,
        pagesVisited: Object.keys(summary.pageVisits || {}),
        lastActivity: new Date(summary.lastActivity)
      };
    } catch (error) {
      console.error('Error getting daily activity summary:', error);
      return null;
    }
  }

  /**
   * Get detailed page navigation history for a user (super admin only)
   */
  async getPageNavigationHistory(username: string, date: string): Promise<any> {
    try {
      const currentUser = authService.getCurrentUser();
      if (!currentUser || currentUser.userType !== 'superadmin') {
        return null;
      }

      const response = await fetch(`${API_BASE_URL}/api/managers/${username}/pages/${date}`);
      const result = await response.json();

      if (!response.ok) {
        console.error('Failed to get page navigation history:', result.error);
        return null;
      }

      return result.data;
    } catch (error) {
      console.error('Error getting page navigation history:', error);
      return null;
    }
  }

  /**
   * Get enhanced activity summary with per-page breakdown
   */
  async getEnhancedActivitySummary(username: string, date: string): Promise<any> {
    try {
      const currentUser = authService.getCurrentUser();
      if (!currentUser || currentUser.userType !== 'superadmin') {
        return null;
      }

      const response = await fetch(`${API_BASE_URL}/api/managers/${username}/activity/${date}`);
      const result = await response.json();

      if (!response.ok) {
        console.error('Failed to get enhanced activity summary:', result.error);
        return null;
      }

      return result.data;
    } catch (error) {
      console.error('Error getting enhanced activity summary:', error);
      return null;
    }
  }

  /**
   * Get all manager activity summaries for a date (super admin only)
   */
  async getAllDailyActivitySummaries(date: string): Promise<DailySummary[]> {
    try {
      // Get all managers first
      const managers = await authService.getAllManagers();
      
      // Get activity summary for each manager
      const summaries = await Promise.all(
        managers.map(async (manager) => {
          const summary = await this.getDailyActivitySummary(manager.username, date);
          return summary;
        })
      );

      return summaries.filter(summary => summary !== null) as DailySummary[];
    } catch (error) {
      console.error('Error getting all daily activity summaries:', error);
      return [];
    }
  }

  /**
   * Format time duration for display
   */
  formatDuration(milliseconds: number): string {
    const hours = Math.floor(milliseconds / (1000 * 60 * 60));
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((milliseconds % (1000 * 60)) / 1000);

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  }

  /**
   * Calculate activity percentage (active time / total time)
   */
  calculateActivityPercentage(activeTimeMs: number, totalIdleMs: number): number {
    const totalTime = activeTimeMs + totalIdleMs;
    if (totalTime === 0) return 100;
    return Math.round((activeTimeMs / totalTime) * 100);
  }
}

export const activityTracker = new ActivityTracker();
export type { ActivityEntry, DailySummary }; 